import { TournamentMatchStatusEnum } from '../types/enums';
import {
  IMatch,
  PlayValues,
} from '../pages/tourDetails/TournamentPlay/components/PlayTag/PlayTag';
import {
  collectPendingDeletedMatchIds,
  getBracketCellId,
} from './bracketCellPendingDeletes.util';

const BRACKET_CELL_STATUS_COMPLETED = 2;

export type StartedDownstreamDeleteCheck = {
  hasStartedDownstream: boolean;
  allStartedDownstreamCoveredByDelete: boolean;
};

function canonicalMatchIdForCell(matches: IMatch[], cellId: string): string {
  const cellMatches = matches.filter((m) => getBracketCellId(m) === cellId);
  const anchor =
    cellMatches.find((m) => m.bracketCellMatchIndex === 1) ?? cellMatches[0];
  return anchor?.matchId ?? '';
}

function isStartedOrHasScores(match: IMatch): boolean {
  if (
    match.status != null &&
    match.status !== TournamentMatchStatusEnum.NOT_STARTED
  ) {
    return true;
  }
  return Array.isArray(match.results) && match.results.length > 0;
}

function placeholdersPointAtCanonical(
  match: IMatch,
  canonicalMatchId: string,
): boolean {
  if (!canonicalMatchId) return false;
  return (
    match.homeTeamPlaceholder === canonicalMatchId ||
    match.awayTeamPlaceholder === canonicalMatchId
  );
}

function isCompletedStatus(status: unknown): boolean {
  return Number(status) === BRACKET_CELL_STATUS_COMPLETED;
}

export function checkStartedDownstreamDeletes(
  values: PlayValues,
  baseline: PlayValues | null | undefined,
): StartedDownstreamDeleteCheck {
  const source = baseline ?? values;
  const baselineMatches = Object.values(source.tournamentMatches ?? {});
  const pendingMatchIds = collectPendingDeletedMatchIds(
    values.bracketCells ?? {},
    values.pendingDeletedTopLevelMatchIds ?? [],
  );
  const pendingCellIds = new Set(values.pendingDeletedBracketCellIds ?? []);

  const cellsToCheck = new Set<string>();
  for (const [cellId, cellForm] of Object.entries(values.bracketCells ?? {})) {
    const hasDeletes = (cellForm.pendingDeletedMatchIds ?? []).length > 0;
    const cellDeleted = pendingCellIds.has(cellId);
    const completed =
      isCompletedStatus(cellForm.status) ||
      isCompletedStatus(source.bracketCells?.[cellId]?.status);
    if (completed && (hasDeletes || cellDeleted)) {
      cellsToCheck.add(cellId);
    }
  }
  for (const cellId of pendingCellIds) {
    if (
      isCompletedStatus(values.bracketCells?.[cellId]?.status) ||
      isCompletedStatus(source.bracketCells?.[cellId]?.status)
    ) {
      cellsToCheck.add(cellId);
    }
  }

  const startedDownstream: IMatch[] = [];
  for (const cellId of cellsToCheck) {
    const canonical = canonicalMatchIdForCell(baselineMatches, cellId);
    for (const match of baselineMatches) {
      if (getBracketCellId(match) === cellId) continue;
      if (!placeholdersPointAtCanonical(match, canonical)) continue;
      if (!isStartedOrHasScores(match)) continue;
      startedDownstream.push(match);
    }
  }

  if (startedDownstream.length === 0) {
    return {
      hasStartedDownstream: false,
      allStartedDownstreamCoveredByDelete: true,
    };
  }

  const allCovered = startedDownstream.every((match) => {
    if (typeof match.id === 'number' && pendingMatchIds.has(match.id)) {
      return true;
    }
    const cellId = getBracketCellId(match);
    return cellId != null && pendingCellIds.has(cellId);
  });

  return {
    hasStartedDownstream: true,
    allStartedDownstreamCoveredByDelete: allCovered,
  };
}
